import { requestPersistentStorage } from "./offline/storageHealth";
import { isOfflineFeatureEnabled } from "./offline/featureFlag";

/**
 * Registers the Workbox-generated service worker, which is what lets the app boot
 * from a cold start with no connectivity at all, and then asks the browser to keep
 * our origin's storage.
 *
 * Production only: in development CRA serves an unhashed bundle and a registered
 * worker would serve stale assets across rebuilds.
 */
export const registerServiceWorker = (): void => {
  if (process.env.NODE_ENV !== "production") return;
  if (!("serviceWorker" in navigator)) return;

  // Not enabled for this user: make sure nothing is left running from a previous
  // session (or from before they were switched off again).
  if (!isOfflineFeatureEnabled()) {
    void unregisterServiceWorker();
    return;
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then(() => requestPersistentStorage())
      .catch((error) => console.error("[SW] registration failed:", error));
  });
};

/**
 * The kill switch.
 *
 * A service worker is sticky: once installed it controls asset loading for the
 * origin on that device, so a bad one can keep serving stale application code and
 * an ordinary deploy will not necessarily dislodge it. This tears down both halves
 * — the worker AND every cache it created — so switching a user off (or shipping a
 * build with the feature off) actually returns them to a plain, uncached app on
 * their next load.
 *
 * Callable from the console for a single stuck device:
 *   window.__connectyidDisableOffline()
 */
export const unregisterServiceWorker = async (): Promise<void> => {
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
    if ("caches" in window) {
      const names = await caches.keys();
      // Only our own caches — never touch another origin's or another tool's.
      await Promise.all(
        names
          .filter((name) => name.startsWith("workbox-") || name.startsWith("connectyid-"))
          .map((name) => caches.delete(name))
      );
    }
  } catch (error) {
    console.error("[SW] teardown failed:", error);
  }
};

// Escape hatch for support: lets someone rescue a single device over the phone
// without waiting for a deploy.
(window as any).__connectyidDisableOffline = async () => {
  await unregisterServiceWorker();
  window.localStorage.setItem("offline_enabled", "0");
  window.location.reload();
};
